import * as z from "zod";
import { decimal, pagination } from "../index";
import { creditOutcomes, soStatuses } from "./enums";

// "Clienti bloccati" (spec v0.2 §9, P8.10a, ADR-214): the customers Credit Control has to look at, with the open orders
// their block holds back. A view of Tomorrow, served by sales, which owns the orders and reads the credit data of the
// customer from party (ADR-082).

/**
 * Why a customer is in the list, one or more:
 * - `on_hold`: its credit is on hold in the directory;
 * - `blacklisted`: it is in the blacklist (B-49 point 3: only Credit Control puts it there and takes it out);
 * - `orders_blocked`: an open order of it has its credit blocked where Credit Control can override it, the same
 *   orders as the queue `creditBlocked` of the list of the orders.
 */
export const blockedCustomerCauses = ["on_hold", "blacklisted", "orders_blocked"] as const;
export type BlockedCustomerCause = (typeof blockedCustomerCauses)[number];

/** The open orders a row carries at most, those blocked for credit first: the rest are counted. */
export const BLOCKED_CUSTOMER_ORDERS = 20;

/** The blocked customers by name, with the open orders of each. */
export const listBlockedCustomersInput = z.object({
  /** Only the customers in the list for this cause. */
  cause: z.enum(blockedCustomerCauses).optional(),
  /** Part of the name of the customer, or its code. */
  q: z.string().trim().min(1).max(100).optional(),
  ...pagination({ defaultSize: 50, maxSize: 100 }),
});
export type ListBlockedCustomersInput = z.input<typeof listBlockedCustomersInput>;

/** An open order of a blocked customer, as the row shows it. */
export const blockedCustomerOrderDto = z.object({
  id: z.uuid(),
  soNumber: z.string(),
  status: z.enum(soStatuses),
  creditOutcome: z.enum(creditOutcomes),
  /** Its credit is blocked where Credit Control can override it (`canOverrideCredit` of the rules). */
  creditBlocked: z.boolean(),
  currency: z.string(),
  /** What it ordered, after the discounts and without the cancelled lines; a field rule may hide it, as on the order. */
  totalAmount: decimal.optional(),
  createdAt: z.iso.datetime(),
});
export type BlockedCustomerOrderDto = z.output<typeof blockedCustomerOrderDto>;

/** A customer blocked for credit, with why and the open orders it holds back. */
export const blockedCustomerDto = z
  .object({
    orgId: z.uuid(),
    customer: z.string(),
    customerCode: z.string().nullable(),
    causes: z.array(z.enum(blockedCustomerCauses)),
    onHold: z.boolean(),
    isBlacklisted: z.boolean(),
    /** Why Credit Control put it in the blacklist; a field rule of the directory may hide it. */
    blacklistReason: z.string().nullable().optional(),
    /** Insured by Hilldun: an order without its approval is blocked (ADR-185). */
    hilldunInsured: z.boolean(),
    /** Orders neither completed nor cancelled. */
    openOrderCount: z.number().int(),
    /** Of those, the ones blocked for credit. */
    blockedOrderCount: z.number().int(),
    /** At most `BLOCKED_CUSTOMER_ORDERS` open orders, those blocked for credit first, then the oldest. */
    orders: z.array(blockedCustomerOrderDto),
  })
  .meta({ id: "BlockedCustomer" });
export type BlockedCustomerDto = z.output<typeof blockedCustomerDto>;
